import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { Visit, Client, User, GPSLocation, Role, Permissions, Order, OrderLine } from '../types';
import { supabaseAdmin } from '../lib/supabase';
import { useAuth } from './AuthContext';
interface AppContextType {
  visits: Visit[];
  clients: Client[];
  users: User[];
  roles: Role[];
  orders: Order[];
  totalVendido: number;
  isLoading: boolean;
  newOrderAlert: boolean;
  clearNewOrderAlert: () => void;
  addVisit: (visit: Omit<Visit, 'id'>) => Promise<void>;
  updateVisit: (id: string, updates: Partial<Visit>) => Promise<void>;
  startVisit: (vendedorId: string, vendedorNombre: string, clienteId: string, tipoVisita: Visit['tipoVisita']) => Visit;
  endVisit: (visitId: string, visitData: Partial<Visit>) => Promise<void>;
  getVisitsByVendedor: (vendedorId: string) => Visit[];
  getVisitsByDate: (date: Date) => Visit[];
  getTodayStats: (vendedorId?: string) => {
    totalVisitas: number;
    totalCobrado: number;
    totalVendido: number;
    tiempoPromedio: number;
    visitasCompletadas: number;
    visitasEnProgreso: number;
  };
  addClient: (client: Omit<Client, 'id'>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  updateClient: (id: string, updates: Partial<Client>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  deleteClient: (id: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  addUser: (user: Omit<User, 'id' | 'fechaCreacion' | 'rolNombre' | 'permisos'>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  updateUser: (id: string, updates: Partial<User>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  deleteUser: (id: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  addRole: (role: Omit<Role, 'id' | 'fechaCreacion'>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  updateRole: (id: string, updates: Partial<Role>) => Promise<{
    success: boolean;
    error?: string;
  }>;
  deleteRole: (id: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  updateOrderStatus: (orderId: string, newStatus: Order['estado']) => Promise<void>;
  getCurrentLocation: () => Promise<GPSLocation | null>;
  refreshData: () => Promise<void>;
}
const AppContext = createContext<AppContextType | undefined>(undefined);
const DR_CENTER = {
  lat: 18.4861,
  lng: -69.9312
};
// ── localStorage cache helpers for instant initial render ─────────────────
const CACHE_KEYS = {
  visits: 'mr_fast_visits',
  clients: 'mr_fast_clients',
  users: 'mr_fast_users',
  roles: 'mr_fast_roles',
  orders: 'mr_fast_orders',
  totalVendido: 'mr_fast_totalVendido',
  timestamp: 'mr_fast_ts'
} as const;
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours max cache age
function saveToCache(key: string, data: any) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // Storage full — silently fail
  }
}
function loadFromCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
function isCacheFresh(): boolean {
  const ts = loadFromCache<number>(CACHE_KEYS.timestamp);
  if (!ts) return false;
  return Date.now() - ts < CACHE_MAX_AGE;
}
function saveCacheTimestamp() {
  saveToCache(CACHE_KEYS.timestamp, Date.now());
}
// ── NO CACHE — always load fresh from Supabase ──────────────────────────────
function mapVisit(row: any): Visit {
  return {
    id: row.id,
    vendedorId: row.vendedor_id,
    vendedorNombre: row.vendedor_nombre,
    clienteId: row.cliente_id,
    clienteNombre: row.cliente_nombre,
    zona: row.zona,
    tipoVisita: row.tipo_visita,
    fecha: new Date(row.fecha),
    horaLlegada: row.hora_llegada ? new Date(row.hora_llegada) : null,
    horaSalida: row.hora_salida ? new Date(row.hora_salida) : null,
    tiempoTotal: row.tiempo_total,
    cobro: {
      huboCobo: false,
      montoCobrado: 0,
      metodoPago: null,
      numeroFactura: '',
      estadoFactura: 'no_pago',
      credito: 0,
      descuento: 0,
      ...(row.cobro || {})
    },
    venta: {
      huboVenta: false,
      montoVenta: 0,
      numeroPedido: '',
      ...(row.venta || {})
    },
    comentarios: row.comentarios || '',
    motivoNoPago: row.motivo_no_pago || '',
    productosVencidos: row.productos_vencidos ?? null,
    devolucionItems: row.devolucion_items || [],
    proximaVisita: row.proxima_visita ? new Date(row.proxima_visita) : null,
    estado: row.estado,
    numeroRecibo: row.numero_recibo || null
  };
}
function mapVisitLite(row: any): Visit {
  return {
    id: row.id,
    vendedorId: row.vendedor_id,
    vendedorNombre: row.vendedor_nombre,
    clienteId: row.cliente_id,
    clienteNombre: row.cliente_nombre,
    zona: row.zona,
    tipoVisita: row.tipo_visita,
    fecha: new Date(row.fecha),
    horaLlegada: row.hora_llegada ? new Date(row.hora_llegada) : null,
    horaSalida: row.hora_salida ? new Date(row.hora_salida) : null,
    tiempoTotal: row.tiempo_total,
    cobro: {
      huboCobo: false,
      montoCobrado: 0,
      metodoPago: null,
      numeroFactura: '',
      estadoFactura: 'no_pago',
      credito: 0,
      descuento: 0,
      ...(row.cobro || {})
    },
    venta: {
      huboVenta: false,
      montoVenta: 0,
      numeroPedido: '',
      ...(row.venta || {})
    },
    comentarios: row.comentarios || '',
    motivoNoPago: row.motivo_no_pago || '',
    productosVencidos: row.productos_vencidos ?? null,
    devolucionItems: [],
    proximaVisita: row.proxima_visita ? new Date(row.proxima_visita) : null,
    estado: row.estado,
    numeroRecibo: row.numero_recibo || null
  };
}
function mapClient(row: any): Client {
  return {
    id: row.id,
    codigo: row.codigo || '',
    nombre: row.nombre,
    telefono: row.telefono || '',
    ciudad: row.ciudad || '',
    limite: row.limite || 0,
    cedula: row.cedula || '',
    rnc: row.rnc || '',
    ruta: row.ruta || '',
    direccion: row.direccion,
    vendedorId: row.vendedor_id || undefined,
    vendedorNombre: row.vendedor_nombre || undefined,
    dependencia: row.dependencia || '',
    zona: row.zona || '',
    lat: row.lat,
    lng: row.lng
  };
}
function mapRole(row: any): Role {
  return {
    id: row.id,
    nombre: row.nombre,
    permisos: row.permisos as Permissions,
    fechaCreacion: new Date(row.created_at)
  };
}
function mapUser(row: any, roleMap: Record<string, Role>): User {
  const role = roleMap[row.rol_id] || {
    nombre: 'Sin Rol',
    permisos: {}
  };
  return {
    id: row.id,
    nombre: row.nombre,
    email: row.email || '',
    password: row.password,
    rolId: row.rol_id,
    rolNombre: role.nombre,
    permisos: role.permisos,
    activo: row.activo,
    avatarUrl: row.avatar_url || '',
    fechaCreacion: new Date(row.fecha_creacion)
  };
}
function mapOrder(row: any): Order {
  return {
    id: row.id,
    numeroPedido: row.numero_pedido,
    farmacia: row.farmacia,
    vendedor: row.vendedor,
    fecha: new Date(row.fecha),
    items: row.items,
    total: row.total,
    estado: row.estado,
    lineas: (row.order_lines || []).map((l: any): OrderLine => ({
      productoId: l.producto_id,
      nombre: l.nombre,
      sku: l.sku,
      cantidad: l.cantidad,
      precio: l.precio,
      itbis: l.itbis || 0,
      stockDisponible: l.stock_disponible
    }))
  };
}
// ── New-order alert: pleasant store-bell chime ───────────────────────────
function playNewOrderAlert() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const chimes = [{
      freq: 880,
      start: 0,
      dur: 0.35
    }, {
      freq: 1108.73,
      start: 0.18,
      dur: 0.35
    }, {
      freq: 1318.51,
      start: 0.36,
      dur: 0.5
    }];
    chimes.forEach(({
      freq,
      start,
      dur
    }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(0.5, ctx.currentTime + start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.value = freq * 2;
      gain2.gain.setValueAtTime(0, ctx.currentTime + start);
      gain2.gain.linearRampToValueAtTime(0.12, ctx.currentTime + start + 0.01);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur * 0.7);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(ctx.currentTime + start);
      osc2.stop(ctx.currentTime + start + dur + 0.05);
    });
    setTimeout(() => {
      try {
        const ctx2 = new (window.AudioContext || (window as any).webkitAudioContext)();
        const osc = ctx2.createOscillator();
        const gain = ctx2.createGain();
        osc.type = 'sine';
        osc.frequency.value = 1318.51;
        gain.gain.setValueAtTime(0, ctx2.currentTime);
        gain.gain.linearRampToValueAtTime(0.3, ctx2.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx2.currentTime + 0.6);
        osc.connect(gain);
        gain.connect(ctx2.destination);
        osc.start(ctx2.currentTime);
        osc.stop(ctx2.currentTime + 0.65);
      } catch (_) {}
    }, 700);
  } catch (_) {}
}
const VISITS_COLUMNS = 'id,vendedor_id,vendedor_nombre,cliente_id,cliente_nombre,zona,tipo_visita,fecha,hora_llegada,hora_salida,tiempo_total,cobro,venta,comentarios,motivo_no_pago,productos_vencidos,proxima_visita,estado,numero_recibo,created_at';
export function AppProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user: authUser
  } = useAuth();
  // ── ONE-TIME: Clear ALL old localStorage cache keys from previous versions ──
  useEffect(() => {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mr_cache_')) {
          keysToRemove.push(key);
        }
      }
      if (keysToRemove.length > 0) {
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log(`[AppContext] Cleaned ${keysToRemove.length} old cache keys`);
      }
    } catch {
      // Silently fail
    }
  }, []);
  // ── Hydrate from cache INSTANTLY, then load fresh from Supabase ───────────
  const cachedVisitsRaw = isCacheFresh() ? loadFromCache<any[]>(CACHE_KEYS.visits) : null;
  const cachedClientsRaw = isCacheFresh() ? loadFromCache<any[]>(CACHE_KEYS.clients) : null;
  const cachedUsersRaw = isCacheFresh() ? loadFromCache<any[]>(CACHE_KEYS.users) : null;
  const cachedRolesRaw = isCacheFresh() ? loadFromCache<any[]>(CACHE_KEYS.roles) : null;
  const cachedOrdersRaw = isCacheFresh() ? loadFromCache<any[]>(CACHE_KEYS.orders) : null;
  const cachedTotalVendido = isCacheFresh() ? loadFromCache<number>(CACHE_KEYS.totalVendido) : null;
  const hasCachedData = !!(cachedVisitsRaw && cachedVisitsRaw.length > 0 && cachedClientsRaw);
  const [visits, setVisits] = useState<Visit[]>(() => {
    if (cachedVisitsRaw) {
      try {
        return cachedVisitsRaw.map((row: any) => ({
          ...row,
          fecha: new Date(row.fecha),
          horaLlegada: row.horaLlegada ? new Date(row.horaLlegada) : null,
          horaSalida: row.horaSalida ? new Date(row.horaSalida) : null,
          proximaVisita: row.proximaVisita ? new Date(row.proximaVisita) : null
        }));
      } catch {
        return [];
      }
    }
    return [];
  });
  const [clients, setClients] = useState<Client[]>(() => cachedClientsRaw || []);
  const [users, setUsers] = useState<User[]>(() => {
    if (cachedUsersRaw) {
      try {
        return cachedUsersRaw.map((u: any) => ({
          ...u,
          fechaCreacion: new Date(u.fechaCreacion)
        }));
      } catch {
        return [];
      }
    }
    return [];
  });
  const [roles, setRoles] = useState<Role[]>(() => {
    if (cachedRolesRaw) {
      try {
        return cachedRolesRaw.map((r: any) => ({
          ...r,
          fechaCreacion: new Date(r.fechaCreacion)
        }));
      } catch {
        return [];
      }
    }
    return [];
  });
  const [orders, setOrders] = useState<Order[]>(() => {
    if (cachedOrdersRaw) {
      try {
        return cachedOrdersRaw.map((o: any) => ({
          ...o,
          fecha: new Date(o.fecha)
        }));
      } catch {
        return [];
      }
    }
    return [];
  });
  const [totalVendido, setTotalVendido] = useState<number>(cachedTotalVendido || 0);
  // If we have cached data, start with isLoading=false for instant render
  const [isLoading, setIsLoading] = useState(!hasCachedData);
  const [newOrderAlert, setNewOrderAlert] = useState(false);
  const clearNewOrderAlert = () => setNewOrderAlert(false);
  // ── Refs for smart refresh ─────────────────────────────────────────────────
  const isLoadingRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const lastLoadTimestampRef = useRef(0);
  const MIN_RELOAD_INTERVAL = 15000; // Reduced from 30s to 15s
  const LAST_PENDING_KEY = 'mr_last_pending_count';
  const pendingCountRef = useRef<number>(-1);
  const LAST_EMPACADO_KEY = 'mr_last_empacado_count';
  const empacadoCountRef = useRef<number>(-1);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const isRetryingRef = useRef(false);
  const getCurrentLocation = (): Promise<GPSLocation | null> => {
    return new Promise(resolve => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(pos => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        timestamp: new Date()
      }), () => resolve(null), {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });
    });
  };
  // ── Helper: fetch totalVendido with caching ────────────────────────────────
  const loadTotalVendido = async () => {
    try {
      // EMERGENCY: Single query with limit instead of while(hasMore) loop
      const {
        data,
        error
      } = await supabaseAdmin.from('invoices').select('monto').not('estado', 'eq', 'anulada').limit(5000);
      if (error) {
        console.error('Error loading totalVendido:', error);
        return;
      }
      const sum = (data || []).reduce((s: number, r: any) => s + (r.monto || 0), 0);
      setTotalVendido(sum);
      saveToCache(CACHE_KEYS.totalVendido, sum);
    } catch (error) {
      console.error('Error loading totalVendido:', error);
    }
  };
  // ── Helper: persist current state to localStorage cache ────────────────────
  const persistToCache = useCallback((visitsData: Visit[], clientsData: Client[], usersData: User[], rolesData: Role[], ordersData: Order[]) => {
    try {
      // Serialize visits (strip heavy fields for cache size)
      const visitsLite = visitsData.map(v => ({
        ...v,
        devolucionItems: []
      }));
      saveToCache(CACHE_KEYS.visits, visitsLite);
      saveToCache(CACHE_KEYS.clients, clientsData);
      saveToCache(CACHE_KEYS.users, usersData);
      saveToCache(CACHE_KEYS.roles, rolesData);
      saveToCache(CACHE_KEYS.orders, ordersData);
      saveCacheTimestamp();
    } catch {
      // Silently fail
    }
  }, []);
  const loadData = async (showSpinner = false, force = false) => {
    if (isLoadingRef.current) return;
    const now = Date.now();
    if (!showSpinner && !force && !isInitialLoadRef.current && now - lastLoadTimestampRef.current < MIN_RELOAD_INTERVAL) return;
    isLoadingRef.current = true;
    lastLoadTimestampRef.current = now;
    isRetryingRef.current = false;
    // Only show spinner if we have NO data at all (no cache either)
    if ((showSpinner || isInitialLoadRef.current) && !hasCachedData) {
      setIsLoading(true);
    }
    // Safety timeout: force isLoading=false after 30 seconds no matter what
    const safetyTimer = setTimeout(() => {
      console.warn('[AppContext] Safety timer fired — forcing isLoading=false');
      setIsLoading(false);
      isLoadingRef.current = false;
      isRetryingRef.current = false;
    }, 30000);
    try {
      const loadStart = Date.now();
      // Only load last 90 days of visits for Dashboard/stats (VisitsPage loads its own data)
      const fetchRecentVisits = async () => {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 90);
        const cutoffISO = cutoff.toISOString();
        const {
          data,
          error
        } = await supabaseAdmin.from('visits').select(VISITS_COLUMNS).gte('fecha', cutoffISO).order('fecha', {
          ascending: false
        });
        if (error) {
          console.error('[AppContext] visits query error:', error);
          return [];
        }
        return data || [];
      };
      // Load lightweight tables first (small, fast queries)
      const [clientsRes, rolesRes, profilesRes, ordersRes] = await Promise.all([supabaseAdmin.from('clients').select('id,codigo,nombre,telefono,ciudad,limite,cedula,rnc,ruta,direccion,vendedor_id,vendedor_nombre,dependencia,zona,lat,lng').order('nombre'), supabaseAdmin.from('roles').select('*').order('nombre'), supabaseAdmin.from('profiles').select('*').order('nombre'), supabaseAdmin.from('orders').select('id,numero_pedido,farmacia,vendedor,fecha,items,total,estado').order('fecha', {
        ascending: false
      }).limit(100)]);
      // Load visits AFTER lightweight tables to avoid 520 overload
      const visitsData = await fetchRecentVisits();
      console.log(`[AppContext] loadData queries took ${Date.now() - loadStart}ms (${visitsData.length} visits)`);
      // Check if ALL queries failed (likely network/520 error)
      const allFailed = visitsData.length === 0 && !clientsRes.data && !rolesRes.data && !profilesRes.data && !ordersRes.data;
      if (allFailed && retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        isRetryingRef.current = true;
        console.warn(`[AppContext] All queries failed. Retry ${retryCountRef.current}/${MAX_RETRIES} in 2s...`);
        setTimeout(() => {
          isLoadingRef.current = false;
          loadData(true, true);
        }, 2000);
        return;
      }
      const otherTablesHaveData = (clientsRes.data?.length || 0) > 0 || (profilesRes.data?.length || 0) > 0 || (rolesRes.data?.length || 0) > 0;
      let newVisits: Visit[] = visits;
      let newClients: Client[] = clients;
      let newUsers: User[] = users;
      let newRoles: Role[] = roles;
      let newOrders: Order[] = orders;
      if (visitsData.length > 0) {
        const mappedVisits = visitsData.map(mapVisitLite);
        setVisits(mappedVisits);
        newVisits = mappedVisits;
        retryCountRef.current = 0;
      } else if (otherTablesHaveData && retryCountRef.current < MAX_RETRIES) {
        // Visits returned 0 but other tables loaded — retry visits separately
        retryCountRef.current++;
        isRetryingRef.current = true;
        console.warn(`[AppContext] Visits returned 0 but other tables have data. Retry ${retryCountRef.current}/${MAX_RETRIES} in 1s...`);
        setTimeout(async () => {
          try {
            const retryData = await fetchRecentVisits();
            console.log(`[AppContext] Visits retry got ${retryData.length} rows`);
            const retryVisits = retryData;
            if (retryVisits.length > 0) {
              const mapped = retryVisits.map(mapVisitLite);
              setVisits(mapped);
              retryCountRef.current = 0;
              isRetryingRef.current = false;
              setIsLoading(false);
              persistToCache(mapped, newClients, newUsers, newRoles, newOrders);
            } else if (retryCountRef.current < MAX_RETRIES) {
              isLoadingRef.current = false;
              isRetryingRef.current = false;
              loadData(false, true);
            } else {
              isRetryingRef.current = false;
              setIsLoading(false);
            }
          } catch (err) {
            console.error('[AppContext] Visits retry failed:', err);
            isRetryingRef.current = false;
            setIsLoading(false);
          }
        }, 1000);
      }
      if (clientsRes.data) {
        newClients = clientsRes.data.map(mapClient);
        setClients(newClients);
      }
      let loadedRoles: Role[] = [];
      if (rolesRes.data) {
        loadedRoles = rolesRes.data.map(mapRole);
        setRoles(loadedRoles);
        newRoles = loadedRoles;
      }
      if (profilesRes.data) {
        const roleMap: Record<string, Role> = {};
        loadedRoles.forEach(r => roleMap[r.id] = r);
        newUsers = profilesRes.data.map(p => mapUser(p, roleMap));
        setUsers(newUsers);
      }
      if (ordersRes.data) {
        newOrders = ordersRes.data.map((row: any) => ({
          ...mapOrder({
            ...row,
            order_lines: []
          })
        }));
        setOrders(newOrders);
      }
      // Persist to localStorage cache for instant next load
      persistToCache(newVisits, newClients, newUsers, newRoles, newOrders);
      // Load totalVendido ASYNCHRONOUSLY (doesn't block UI)
      loadTotalVendido();
    } catch (error) {
      console.error('Error loading data:', error);
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        isRetryingRef.current = true;
        console.warn(`[AppContext] Catch retry ${retryCountRef.current}/${MAX_RETRIES} in 2s...`);
        setTimeout(() => {
          isLoadingRef.current = false;
          loadData(true, true);
        }, 2000);
        return;
      }
    } finally {
      clearTimeout(safetyTimer);
      isLoadingRef.current = false;
      isInitialLoadRef.current = false;
      // Only release loading state if we're NOT about to retry
      if (!isRetryingRef.current) {
        setIsLoading(false);
      }
    }
  };
  // ── Individual table refreshers ────────────────────────────────────────────
  const refreshVisits = async () => {
    try {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 90);
      const {
        data,
        error
      } = await supabaseAdmin.from('visits').select(VISITS_COLUMNS).gte('fecha', cutoff.toISOString()).order('fecha', {
        ascending: false
      });
      if (error) {
        console.error('[refreshVisits] Error:', error);
        return;
      }
      if (data && data.length > 0) setVisits(data.map(mapVisitLite));
    } catch (err) {
      console.error('[refreshVisits] Error:', err);
    }
  };
  const refreshClients = async () => {
    const {
      data
    } = await supabaseAdmin.from('clients').select('id,codigo,nombre,telefono,ciudad,limite,cedula,rnc,ruta,direccion,vendedor_id,vendedor_nombre,dependencia,zona,lat,lng').order('nombre');
    if (data) setClients(data.map(mapClient));
  };
  const refreshRolesAndUsers = async () => {
    const [rolesRes, profilesRes] = await Promise.all([supabaseAdmin.from('roles').select('*').order('nombre'), supabaseAdmin.from('profiles').select('*').order('nombre')]);
    let loadedRoles: Role[] = [];
    if (rolesRes.data) {
      loadedRoles = rolesRes.data.map(mapRole);
      setRoles(loadedRoles);
    }
    if (profilesRes.data) {
      const roleMap: Record<string, Role> = {};
      loadedRoles.forEach(r => roleMap[r.id] = r);
      setUsers(profilesRes.data.map(p => mapUser(p, roleMap)));
    }
  };
  const refreshOrders = async () => {
    const {
      data
    } = await supabaseAdmin.from('orders').select('id,numero_pedido,farmacia,vendedor,fecha,items,total,estado').order('fecha', {
      ascending: false
    }).limit(100);
    if (data) setOrders(data.map((row: any) => ({
      ...mapOrder({
        ...row,
        order_lines: []
      })
    })));
  };
  useEffect(() => {
    // Initial load — fetch fresh data (cache already hydrated above)
    loadData(true);
    // ── EMERGENCY: Realtime channels DISABLED to reduce egress ──────────────
    // The 3 channels (visits-ch, clients-ch, orders-ch) were consuming massive
    // egress. They are disabled until the Supabase plan is upgraded or egress
    // is under control. Data updates via polling only.
    // Polling fallback — every 15 minutes (was 5 min)
    const pollInterval = setInterval(() => {
      loadData(false, true);
    }, 900000);
    // Refresh on window focus (only if tab was hidden for > 5 minutes, was 30s)
    let lastHiddenAt = 0;
    const handleVisibility = () => {
      if (document.hidden) lastHiddenAt = Date.now();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    const handleFocus = () => {
      if (lastHiddenAt > 0 && Date.now() - lastHiddenAt > 300000) {
        loadData(false, true);
      }
    };
    window.addEventListener('focus', handleFocus);
    const handleOnline = () => {
      console.log('[Network] Back online — refreshing data');
      loadData(false, true);
    };
    window.addEventListener('online', handleOnline);
    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, []);
  // ── Global pending-order monitor ──────────────────────────────────────
  const isRepartidor = authUser?.permisos?.dashboard_entregas === true && authUser?.rolNombre !== 'Administrador';
  const isCliente = authUser?.rolNombre === 'Cliente';
  useEffect(() => {
    if (isRepartidor) return;
    if (isCliente) return;
    const pendingOrders = orders.filter(o => o.estado === 'pendiente');
    const currentPendingCount = pendingOrders.length;
    const lastSeen = parseInt(localStorage.getItem(LAST_PENDING_KEY) || '-1', 10);
    if (lastSeen === -1) {
      localStorage.setItem(LAST_PENDING_KEY, String(currentPendingCount));
      pendingCountRef.current = currentPendingCount;
    } else if (currentPendingCount > lastSeen) {
      playNewOrderAlert();
      setNewOrderAlert(true);
      localStorage.setItem(LAST_PENDING_KEY, String(currentPendingCount));
      pendingCountRef.current = currentPendingCount;
    } else {
      if (currentPendingCount !== lastSeen) {
        localStorage.setItem(LAST_PENDING_KEY, String(currentPendingCount));
      }
      pendingCountRef.current = currentPendingCount;
    }
  }, [orders]);
  // ── Repartidor monitor ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isRepartidor) return;
    const empacadoOrders = orders.filter(o => o.estado === 'empacado');
    const currentEmpacadoCount = empacadoOrders.length;
    const lastSeen = parseInt(localStorage.getItem(LAST_EMPACADO_KEY) || '-1', 10);
    if (lastSeen === -1) {
      localStorage.setItem(LAST_EMPACADO_KEY, String(currentEmpacadoCount));
      empacadoCountRef.current = currentEmpacadoCount;
    } else if (currentEmpacadoCount > lastSeen) {
      playNewOrderAlert();
      setNewOrderAlert(true);
      localStorage.setItem(LAST_EMPACADO_KEY, String(currentEmpacadoCount));
      empacadoCountRef.current = currentEmpacadoCount;
    } else {
      if (currentEmpacadoCount !== lastSeen) {
        localStorage.setItem(LAST_EMPACADO_KEY, String(currentEmpacadoCount));
      }
      empacadoCountRef.current = currentEmpacadoCount;
    }
  }, [orders, isRepartidor]);
  const addVisit = useCallback(async (visit: Omit<Visit, 'id'>) => {
    const {
      data
    } = await supabaseAdmin.from('visits').insert({
      vendedor_id: visit.vendedorId,
      vendedor_nombre: visit.vendedorNombre,
      cliente_id: visit.clienteId,
      cliente_nombre: visit.clienteNombre,
      zona: visit.zona,
      tipo_visita: visit.tipoVisita,
      fecha: visit.fecha.toISOString(),
      hora_llegada: visit.horaLlegada?.toISOString() || null,
      hora_salida: visit.horaSalida?.toISOString() || null,
      tiempo_total: visit.tiempoTotal,
      cobro: visit.cobro,
      venta: visit.venta,
      comentarios: visit.comentarios,
      motivo_no_pago: visit.motivoNoPago,
      proxima_visita: visit.proximaVisita?.toISOString() || null,
      estado: visit.estado
    }).select().single();
    if (data) setVisits(prev => [mapVisit(data), ...prev]);
  }, []);
  const updateVisit = useCallback(async (id: string, updates: Partial<Visit>) => {
    const finalUpdates: any = {};
    if (updates.cobro) finalUpdates.cobro = updates.cobro;
    if (updates.venta) finalUpdates.venta = updates.venta;
    if (updates.comentarios !== undefined) finalUpdates.comentarios = updates.comentarios;
    if (updates.motivoNoPago !== undefined) finalUpdates.motivo_no_pago = updates.motivoNoPago;
    if (updates.estado) finalUpdates.estado = updates.estado;
    if (updates.horaSalida) finalUpdates.hora_salida = updates.horaSalida.toISOString();
    if (updates.tiempoTotal !== undefined) finalUpdates.tiempo_total = updates.tiempoTotal;
    if (updates.proximaVisita) finalUpdates.proxima_visita = updates.proximaVisita.toISOString();
    const {
      data
    } = await supabaseAdmin.from('visits').update(finalUpdates).eq('id', id).select().single();
    if (data) setVisits(prev => prev.map(v => v.id === id ? mapVisit(data) : v));
  }, []);
  const startVisit = useCallback((vendedorId: string, vendedorNombre: string, clienteId: string, tipoVisita: Visit['tipoVisita']): Visit => {
    const client = clients.find(c => c.id === clienteId);
    const tempId = `temp-${Date.now()}`;
    const newVisit: Visit = {
      id: tempId,
      vendedorId,
      vendedorNombre,
      clienteId,
      clienteNombre: client?.nombre || '',
      zona: client?.zona || '',
      tipoVisita,
      fecha: new Date(),
      horaLlegada: new Date(),
      horaSalida: null,
      tiempoTotal: null,
      cobro: {
        huboCobo: false,
        montoCobrado: 0,
        metodoPago: null,
        numeroFactura: '',
        estadoFactura: 'no_pago',
        credito: 0,
        descuento: 0
      },
      venta: {
        huboVenta: false,
        montoVenta: 0,
        numeroPedido: ''
      },
      comentarios: '',
      motivoNoPago: '',
      proximaVisita: null,
      estado: 'en_progreso'
    };
    setVisits(prev => [newVisit, ...prev]);
    const save = async () => {
      const location = await getCurrentLocation();
      const {
        data
      } = await supabaseAdmin.from('visits').insert({
        vendedor_id: vendedorId,
        vendedor_nombre: vendedorNombre,
        cliente_id: clienteId,
        cliente_nombre: client?.nombre || '',
        zona: client?.zona || '',
        tipo_visita: tipoVisita,
        fecha: new Date().toISOString(),
        hora_llegada: new Date().toISOString(),
        cobro: newVisit.cobro,
        venta: newVisit.venta,
        estado: 'en_progreso'
      }).select().single();
      if (data) setVisits(prev => prev.map(v => v.id === tempId ? mapVisit(data) : v));
    };
    save();
    return newVisit;
  }, [clients]);
  const endVisit = useCallback(async (visitId: string, visitData: Partial<Visit>) => {
    const now = new Date();
    const visit = visits.find(v => v.id === visitId);
    const horaLlegada = visit?.horaLlegada || now;
    const tiempoTotal = Math.round((now.getTime() - horaLlegada.getTime()) / 60000);
    const {
      data,
      error
    } = await supabaseAdmin.from('visits').update({
      cobro: visitData.cobro,
      venta: visitData.venta,
      comentarios: visitData.comentarios || '',
      motivo_no_pago: visitData.motivoNoPago || '',
      productos_vencidos: visitData.productosVencidos ?? null,
      devolucion_items: visitData.devolucionItems || [],
      proxima_visita: visitData.proximaVisita?.toISOString() || null,
      hora_salida: now.toISOString(),
      tiempo_total: tiempoTotal,
      estado: 'completada'
    }).eq('id', visitId).select().single();
    if (error) {
      console.error('[endVisit] Error cerrando visita:', error);
      throw new Error(`Error al cerrar la visita: ${error.message}`);
    }
    if (data) setVisits(prev => prev.map(v => v.id === visitId ? mapVisit(data) : v));
  }, [visits]);
  const getVisitsByVendedor = useCallback((vendedorId: string) => visits.filter(v => v.vendedorId === vendedorId), [visits]);
  const getVisitsByDate = useCallback((date: Date) => visits.filter(v => new Date(v.fecha).toDateString() === date.toDateString()), [visits]);
  const getTodayStats = useCallback((vendedorId?: string) => {
    const today = new Date();
    let todayVisits = visits.filter(v => new Date(v.fecha).toDateString() === today.toDateString());
    if (vendedorId) todayVisits = todayVisits.filter(v => v.vendedorId === vendedorId);
    const completedVisits = todayVisits.filter(v => v.estado === 'completada');
    const totalCobrado = todayVisits.reduce((sum, v) => {
      if ((v.cobro as any)?.anulado) return sum;
      if (!v.cobro.huboCobo || v.cobro.montoCobrado <= 0) return sum;
      const savedDesc = v.cobro.descuento || 0;
      if (savedDesc > 0) {
        const credito = v.cobro.credito || 0;
        if (credito > 0 && Math.abs(v.cobro.montoCobrado - credito) < 0.01) {
          return sum + Math.round((v.cobro.montoCobrado - savedDesc) * 100) / 100;
        }
        return sum + v.cobro.montoCobrado;
      }
      return sum + v.cobro.montoCobrado;
    }, 0);
    const totalVendido = todayVisits.reduce((sum, v) => sum + v.venta.montoVenta, 0);
    const tiemposValidos = completedVisits.filter(v => v.tiempoTotal !== null);
    const tiempoPromedio = tiemposValidos.length > 0 ? tiemposValidos.reduce((sum, v) => sum + (v.tiempoTotal || 0), 0) / tiemposValidos.length : 0;
    return {
      totalVisitas: todayVisits.length,
      totalCobrado,
      totalVendido,
      tiempoPromedio: Math.round(tiempoPromedio),
      visitasCompletadas: completedVisits.length,
      visitasEnProgreso: todayVisits.filter(v => v.estado === 'en_progreso').length
    };
  }, [visits]);
  const addClient = useCallback(async (clientData: Omit<Client, 'id'>) => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('clients').insert({
        codigo: clientData.codigo || '',
        nombre: clientData.nombre,
        telefono: clientData.telefono,
        ciudad: clientData.ciudad || '',
        limite: clientData.limite || 0,
        cedula: clientData.cedula || '',
        rnc: clientData.rnc || '',
        ruta: clientData.ruta || '',
        direccion: clientData.direccion,
        vendedor_id: clientData.vendedorId || null,
        vendedor_nombre: clientData.vendedorNombre || null,
        dependencia: clientData.dependencia || '',
        zona: clientData.zona || '',
        lat: clientData.lat || null,
        lng: clientData.lng || null
      }).select().single();
      if (error) return {
        success: false,
        error: error.message
      };
      if (data) setClients(prev => [...prev, mapClient(data)].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const updateClient = useCallback(async (id: string, updates: Partial<Client>) => {
    try {
      const dbUpdates: any = {};
      if (updates.codigo !== undefined) dbUpdates.codigo = updates.codigo;
      if (updates.nombre !== undefined) dbUpdates.nombre = updates.nombre;
      if (updates.telefono !== undefined) dbUpdates.telefono = updates.telefono;
      if (updates.ciudad !== undefined) dbUpdates.ciudad = updates.ciudad;
      if (updates.limite !== undefined) dbUpdates.limite = updates.limite;
      if (updates.cedula !== undefined) dbUpdates.cedula = updates.cedula;
      if (updates.rnc !== undefined) dbUpdates.rnc = updates.rnc;
      if (updates.ruta !== undefined) dbUpdates.ruta = updates.ruta;
      if (updates.direccion !== undefined) dbUpdates.direccion = updates.direccion;
      if (updates.vendedorId !== undefined) dbUpdates.vendedor_id = updates.vendedorId;
      if (updates.vendedorNombre !== undefined) dbUpdates.vendedor_nombre = updates.vendedorNombre;
      if (updates.dependencia !== undefined) dbUpdates.dependencia = updates.dependencia;
      if (updates.zona !== undefined) dbUpdates.zona = updates.zona;
      if (updates.lat !== undefined) dbUpdates.lat = updates.lat;
      if (updates.lng !== undefined) dbUpdates.lng = updates.lng;
      const {
        data,
        error
      } = await supabaseAdmin.from('clients').update(dbUpdates).eq('id', id).select().single();
      if (error) return {
        success: false,
        error: error.message
      };
      if (data) setClients(prev => prev.map(c => c.id === id ? mapClient(data) : c));
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const deleteClient = useCallback(async (id: string) => {
    try {
      const {
        error
      } = await supabaseAdmin.from('clients').delete().eq('id', id);
      if (error) return {
        success: false,
        error: error.message
      };
      setClients(prev => prev.filter(c => c.id !== id));
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const addUser = useCallback(async (userData: Omit<User, 'id' | 'fechaCreacion' | 'rolNombre' | 'permisos'>) => {
    try {
      const matchedRole = roles.find(r => r.id === userData.rolId);
      const insertData: any = {
        nombre: userData.nombre,
        password: userData.password,
        rol_id: userData.rolId,
        rol: matchedRole?.nombre || 'vendedor',
        activo: userData.activo
      };
      if (userData.email) insertData.email = userData.email.trim().toLowerCase();
      const {
        data,
        error
      } = await supabaseAdmin.from('profiles').insert(insertData).select().single();
      if (error) return {
        success: false,
        error: error.message
      };
      if (data) await refreshRolesAndUsers();
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, [roles]);
  const updateUser = useCallback(async (id: string, updates: Partial<User>) => {
    try {
      const dbUpdates: any = {};
      if (updates.nombre !== undefined) dbUpdates.nombre = updates.nombre;
      if (updates.email !== undefined) dbUpdates.email = updates.email?.trim().toLowerCase() || null;
      if (updates.password !== undefined) dbUpdates.password = updates.password;
      if (updates.rolId !== undefined) {
        dbUpdates.rol_id = updates.rolId;
        const matchedRole = roles.find(r => r.id === updates.rolId);
        if (matchedRole) dbUpdates.rol = matchedRole.nombre;
      }
      if (updates.activo !== undefined) dbUpdates.activo = updates.activo;
      const {
        error
      } = await supabaseAdmin.from('profiles').update(dbUpdates).eq('id', id);
      if (error) {
        if (error.message.includes('email')) {
          delete dbUpdates.email;
          const {
            error: error2
          } = await supabaseAdmin.from('profiles').update(dbUpdates).eq('id', id);
          if (error2) return {
            success: false,
            error: error2.message
          };
          await refreshRolesAndUsers();
          return {
            success: true
          };
        }
        return {
          success: false,
          error: error.message
        };
      }
      await refreshRolesAndUsers();
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, [roles]);
  const deleteUser = useCallback(async (id: string) => {
    try {
      const {
        error
      } = await supabaseAdmin.from('profiles').delete().eq('id', id);
      if (error) return {
        success: false,
        error: error.message
      };
      setUsers(prev => prev.filter(u => u.id !== id));
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const addRole = useCallback(async (role: Omit<Role, 'id' | 'fechaCreacion'>) => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('roles').insert({
        nombre: role.nombre,
        permisos: role.permisos
      }).select().single();
      if (error) return {
        success: false,
        error: error.message
      };
      if (data) setRoles(prev => [...prev, mapRole(data)]);
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const updateRole = useCallback(async (id: string, updates: Partial<Role>) => {
    try {
      const dbUpdates: any = {};
      if (updates.nombre) dbUpdates.nombre = updates.nombre;
      if (updates.permisos) dbUpdates.permisos = updates.permisos;
      const {
        error
      } = await supabaseAdmin.from('roles').update(dbUpdates).eq('id', id);
      if (error) return {
        success: false,
        error: error.message
      };
      setRoles(prev => prev.map(r => r.id === id ? {
        ...r,
        ...updates
      } : r));
      await refreshRolesAndUsers();
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const deleteRole = useCallback(async (id: string) => {
    try {
      const {
        error
      } = await supabaseAdmin.from('roles').delete().eq('id', id);
      if (error) return {
        success: false,
        error: error.message
      };
      setRoles(prev => prev.filter(r => r.id !== id));
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }, []);
  const updateOrderStatus = useCallback(async (orderId: string, newStatus: Order['estado']) => {
    setOrders(prev => prev.map(o => o.id === orderId ? {
      ...o,
      estado: newStatus
    } : o));
    await supabaseAdmin.from('orders').update({
      estado: newStatus
    }).eq('id', orderId);
  }, []);
  return <AppContext.Provider value={{
    visits,
    clients,
    users,
    roles,
    orders,
    totalVendido,
    isLoading,
    newOrderAlert,
    clearNewOrderAlert,
    addVisit,
    updateVisit,
    startVisit,
    endVisit,
    getVisitsByVendedor,
    getVisitsByDate,
    getTodayStats,
    addClient,
    updateClient,
    deleteClient,
    addUser,
    updateUser,
    deleteUser,
    addRole,
    updateRole,
    deleteRole,
    updateOrderStatus,
    getCurrentLocation,
    refreshData: () => loadData(false, true)
  }} data-id="element-305">
      {children}
    </AppContext.Provider>;
}
export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) throw new Error('useApp debe usarse dentro de AppProvider');
  return context;
}